import { useContext, useEffect } from 'react';
import {
  useIdVerificationWebApiHandler_getTermSet,
  useIdVerificationWebApiHandler_isIpAddressInTheUs,
  useIdVerificationWebApiHandler_isUserIdentified,
} from '@zg-rentals/ts-id-verification-client-react-query';
import { IdentificationStatusType } from '@zg-rentals/ts-id-verification-client';
import type { WizardStep } from '../wizard/wizardTypes';
import { ErrorWizardStepId, WizardStepId } from '../wizard/wizardTypes';
import IdVerificationContext, {
  useId,
  useIdVerificationCallbacks,
  useSetHasInitializedWizard,
  useTermSet,
} from '../IdvContext';
import { ExitReason, LogLevel } from '../../../models';
import { getZgAuthZoneFromTermSet } from '../utils/zgAuthZoneUtils';
import { termSetToTermSetId } from '../utils/termSetUtils';
import { allTermsAccepted } from './Steps/2Terms/TermsStep';
import { WizardContext } from '../wizard/WizardContext';
import { STEPS } from '../wizard/wizardSteps';

export default function InitializeWizard() {
  const { goToStep, goToErrorStep, setActiveSteps } = useContext(WizardContext);
  const { onExit, log } = useIdVerificationCallbacks();
  const { hasInitializedWizard, skipLandingStep, reauthenticating } = useContext(IdVerificationContext);
  const setHasInitializedWizard = useSetHasInitializedWizard();

  const requestingClient = useId();
  const termSet = useTermSet();
  const termSetId = termSetToTermSetId(termSet);

  const {
    data: isIpAddressInTheUsData,
    error: isIpAddressInTheUsError,
    isFetching: isIpAddressInTheUsFetching,
  } = useIdVerificationWebApiHandler_isIpAddressInTheUs();

  const {
    data: isUserIdentifiedData,
    error: isUserIdentifiedError,
    isFetching: isUserIdentifiedFetching,
  } = useIdVerificationWebApiHandler_isUserIdentified({
    requestingClient,
    zgAuthZone: getZgAuthZoneFromTermSet(termSet),
  });

  const {
    data: getTermSetData,
    error: getTermSetError,
    isFetching: getTermSetFetching,
  } = useIdVerificationWebApiHandler_getTermSet({ termSetId });

  const isLoading = isIpAddressInTheUsFetching || isUserIdentifiedFetching || getTermSetFetching;

  const hasErrored = isIpAddressInTheUsError || isUserIdentifiedError || getTermSetError;

  const identificationStatusType = reauthenticating
    ? IdentificationStatusType.REAUTHENTICATING
    : isUserIdentifiedData?.identificationStatusType;

  const areAllTermsAccepted = allTermsAccepted(getTermSetData?.terms) ?? false;

  useEffect(() => {
    (async () => {
      if (!isLoading) {
        if (hasErrored) {
          log(LogLevel.Error, {
            message: `ID Verification wizard failed to initialize`,
            error: `\nisIpAddressInTheUs: ${isIpAddressInTheUsError}\nisUserIdentified: ${isUserIdentifiedError}\ngetTermSet: ${getTermSetError}`,
          });
          goToErrorStep(ErrorWizardStepId.Generic);
          setHasInitializedWizard(true);
          return;
        }

        if (!hasInitializedWizard) {
          if (!isIpAddressInTheUsData) {
            goToErrorStep(ErrorWizardStepId.ForbiddenIp);
            setHasInitializedWizard(true);
            return;
          }

          let steps: Array<WizardStep> = [];

          switch (identificationStatusType) {
            case IdentificationStatusType.USER_NOT_FOUND_LOCKED_OUT:
              goToErrorStep(ErrorWizardStepId.UserNotFoundLockedOut);
              setHasInitializedWizard(true);
              return;
            case IdentificationStatusType.LOCKED_OUT:
              goToErrorStep(ErrorWizardStepId.UserLockedOut);
              setHasInitializedWizard(true);
              return;
            case IdentificationStatusType.FROZEN_CREDIT_FILE:
              goToErrorStep(ErrorWizardStepId.FrozenAccountWarning);
              setHasInitializedWizard(true);
              return;
            case IdentificationStatusType.CURRENTLY_IDENTIFIED:
              steps = STEPS.filter((step) => {
                if (step.id === WizardStepId.Terms && !areAllTermsAccepted) {
                  return true;
                }

                return false;
              });
              setActiveSteps(steps);
              if (!areAllTermsAccepted) {
                setHasInitializedWizard(true);
                goToStep(steps[0].id);
              } else {
                log(LogLevel.Warn, {
                  message: 'User is already identified',
                });
                await onExit({ reason: ExitReason.CurrentlyIdentified, success: true });
              }
              return;
            case IdentificationStatusType.NOT_IDENTIFIED:
            case IdentificationStatusType.PREVIOUSLY_IDENTIFIED:
            case IdentificationStatusType.REAUTHENTICATING:
              steps = STEPS.filter((step) => {
                if (step.id === WizardStepId.Landing && skipLandingStep) {
                  return false;
                }

                if (step.id === WizardStepId.Terms && areAllTermsAccepted) {
                  return false;
                }

                return true;
              });
              setActiveSteps(steps);
              setHasInitializedWizard(true);
              goToStep(steps[0].id);
              return;
            default:
              log(LogLevel.Warn, {
                message: `Unknown identificationStatusType: ${identificationStatusType}`,
              });
              goToErrorStep(ErrorWizardStepId.Generic);
              return;
          }
        }
      }
    })();
  }, [
    isLoading,
    hasInitializedWizard,
    identificationStatusType,
    areAllTermsAccepted,
    isIpAddressInTheUsData,
    isUserIdentifiedError,
    getTermSetError,
    isIpAddressInTheUsError,
  ]);

  return null;
}
